import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { JobFilters, type FilterType } from '../components/JobFilters';
import { JobForm } from '../components/JobForm';
import { useJobs } from '../hooks/useJobs';
import { useReferenceData } from '@/features/leads/hooks/useReferenceData';
import { getJob } from '../api/jobs-api';
import { usePageTitle } from '@/hooks/usePageTitle';
import type { JobDetailDto, JobsQueryParams } from '../types';

export function JobsPage() {
  usePageTitle('Jobs');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlJobId = searchParams.get('selected');
  const [queryParams, setQueryParams] = useState<JobsQueryParams>({
    status: 'Sold',
  });
  const [currentFilter, setCurrentFilter] = useState<FilterType>('sold');
  const [selectedJob, setSelectedJob] = useState<JobDetailDto | null>(null);

  // Initialize selectedJobId from URL query param if present
  const initialJobId = urlJobId ? parseInt(urlJobId, 10) : undefined;
  const [selectedJobId, setSelectedJobId] = useState<number | undefined>(
    initialJobId && !isNaN(initialJobId) ? initialJobId : undefined
  );

  // Track if we've loaded the initial URL job
  const hasLoadedUrlJob = useRef(false);

  const { data: jobs, isLoading, error, refetch } = useJobs(queryParams);
  const { users } = useReferenceData();

  const loadJob = useCallback(async (jobId: number) => {
    try {
      const fullJob = await getJob(jobId);
      setSelectedJob(fullJob);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      alert(error.response?.data?.message || 'Failed to load job details');
    }
  }, []);

  // Load job from URL param if provided (only on initial mount)
  useEffect(() => {
    if (urlJobId && !hasLoadedUrlJob.current) {
      const jobId = parseInt(urlJobId, 10);
      if (!isNaN(jobId)) {
        hasLoadedUrlJob.current = true;
        // Use setTimeout to avoid synchronous setState in effect
        setTimeout(() => loadJob(jobId), 0);
      }
    }
  }, [urlJobId, loadJob]);

  const handleJobSelect = (jobId: number | undefined) => {
    setSelectedJobId(jobId);
    if (jobId) {
      hasLoadedUrlJob.current = true;
      navigate(`/jobs?selected=${jobId}`, { replace: true });
      loadJob(jobId);
    } else {
      navigate('/jobs', { replace: true });
      setSelectedJob(null);
    }
  };

  const handleFilterChange = (filter: FilterType) => {
    setCurrentFilter(filter);

    // Update query params based on filter
    switch (filter) {
      case 'sold':
        setQueryParams({
          status: 'Sold',
        });
        break;
      case 'closed':
        setQueryParams({
          status: 'Closed',
        });
        break;
      case 'all':
        setQueryParams({
          status: undefined,
        });
        break;
      case 'by-estimator':
        // Already handled by onAssignedToChange
        break;
    }
  };

  const handleAssignedToChange = (userId: number | undefined) => {
    setQueryParams((prev) => ({
      ...prev,
      assignedToId: userId,
    }));
  };

  const handleFormSuccess = () => {
    refetch();
    if (selectedJobId) {
      loadJob(selectedJobId);
    }
  };

  const handleViewLead = () => {
    if (selectedJob) {
      navigate(`/leads/${selectedJob.leadId}`);
    }
  };

  return (
    <div className="bg-background px-5 py-6 space-y-4">
      {/* Main Content Container */}
      <div className="bg-card rounded-lg p-5 space-y-4">
        {/* Job Management Header Section */}
        <div className="space-y-3">
          {/* Job Management Title */}
          <h2 className="text-lg font-semibold text-secondary">Job Management</h2>

          {/* Selected Job and Action Buttons Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Selected Job Dropdown */}
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-black">Selected Job</span>
              <div className="w-[350px]">
                <Select
                  value={selectedJobId?.toString() || ''}
                  onChange={(e) => handleJobSelect(e.target.value ? Number(e.target.value) : undefined)}
                  className="h-8 rounded-lg border-[1.5px] border-border px-3 text-sm font-medium"
                >
                  <option value="">Select Job</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.jobNumber} - {job.leadTitle}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-1.5 ml-auto">
              <Button
                onClick={handleViewLead}
                disabled={!selectedJob}
                variant="ghost"
                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-black hover:bg-gray-50 disabled:opacity-50"
              >
                <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 8V24M8 16H24" stroke="#d71f20" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                View Lead
              </Button>

              <Button
                variant="ghost"
                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-black hover:bg-gray-50"
                onClick={() => navigate('/jobs/search')}
              >
                <svg width="25" height="23" viewBox="0 0 33 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="13" cy="13" r="10" stroke="#d71f20" strokeWidth="2" fill="none"/>
                  <path d="M20 20L30 30" stroke="#d71f20" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Search
              </Button>
            </div>
          </div>
        </div>

        {/* Filter Pills Row */}
        <div className="flex items-center gap-1.5">
          <JobFilters
            onFilterChange={handleFilterChange}
            onAssignedToChange={handleAssignedToChange}
            currentFilter={currentFilter}
            users={users}
            selectedAssignedToId={queryParams.assignedToId}
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D71F20] mx-auto"></div>
          <p className="mt-3 text-muted-foreground text-sm">Loading jobs...</p>
        </div>
      )}

      <ErrorAlert error={error} className="rounded-lg p-2" />

      {/* Job Form - Inline Display */}
      {!isLoading && !error && selectedJob && (
        <JobForm
          job={selectedJob}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Empty State */}
      {!isLoading && !error && !selectedJob && jobs.length > 0 && (
        <div className="text-center py-8">
          <p className="text-muted-foreground text-sm">Select a job from the dropdown above to view details</p>
        </div>
      )}

      {/* No Jobs State */}
      {!isLoading && !error && jobs.length === 0 && (
        <div className="text-center py-8">
          <p className="text-muted-foreground text-sm">No jobs found matching the current filter</p>
        </div>
      )}
    </div>
  );
}
